import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { authService } from '@/services/auth.service';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState('Verifying your account...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setStatus('Getting session...');
        
        // Get both code and code verifier
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        
        // Only attempt code exchange if we have a code
        if (code) {
          setStatus('Exchanging session...');
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) throw error;
          if (!data.session) throw new Error('No session after code exchange');

          setStatus('Finalising setup...');
          const response = await authService.handleAuthCallback(data.session.access_token);
          
          if (response?.profile) {
            setUser(response.profile);
            navigate('/dashboard');
            return;
          }
        }

        // If no code, check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setStatus('Finalising setup...');
          const response = await authService.handleAuthCallback(session.access_token);
          
          if (response?.profile) {
            setUser(response.profile);
            navigate('/dashboard');
          }
        }
      } catch (error: any) {
        if (!error.message?.includes('code verifier should be non-empty')) {
          console.error('Auth callback error:', error);
          toast({
            title: 'Authentication Error',
            description: error.message || 'Failed to complete authentication',
            variant: 'destructive',
          });
          navigate('/auth');
        }
      }
    };

    handleCallback();
  }, [navigate, setUser, toast]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="animate-spin mb-4">
        <Loader2 className="h-8 w-8" />
      </div>
      <p className="text-lg">{status}</p>
    </div>
  );
} 